/**
 * EmailVerificationBanner — belka dla zalogowanych z NIEpotwierdzonym e-mailem.
 *
 * Tryb MIĘKKI (patrz apps/api/.../email-verification.routes.ts): flaga
 * `user_metadata.af_email_verified` to tylko nudge — generowanie NIE jest blokowane.
 * Brak klucza = grandfathering (legacy/OAuth userzy) → belka ukryta. Pokazujemy ją
 * wyłącznie gdy flaga jest jawnie `false`.
 *
 * Montowana w AppShell nad <Topbar> (wewnątrz .app__main, żeby nie rozwalić gridu .app),
 * NAD FeedbackBanner — priorytet, nie dublujemy dwóch belek naraz.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { sendVerificationEmail } from "@/lib/apiClient";

export function EmailVerificationBanner() {
  const { user, isLoading } = useAuth();
  // Brak klucza → traktujemy jak zweryfikowanego (grandfathering).
  const emailVerified = user?.user_metadata?.af_email_verified !== false;
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  const show = !!user && !isLoading && !emailVerified;
  if (!show) return null;

  const handleResend = async () => {
    setIsSending(true);
    try {
      const res = await sendVerificationEmail();
      if ("error" in res) {
        toast.error(res.error || "Nie udało się wysłać linku. Spróbuj za chwilę.");
        return;
      }
      if (res.data?.alreadyVerified) {
        toast.success("Twój e-mail jest już potwierdzony — odśwież stronę.");
        return;
      }
      setSent(true);
      toast.success("Wysłaliśmy link weryfikacyjny. Sprawdź skrzynkę (i SPAM).");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px 10px",
        padding: "10px 16px",
        background: "var(--warning, #f59e0b)",
        color: "#1c1300",
        fontSize: 13,
        fontWeight: 600,
        textAlign: "center",
        flexShrink: 0,
      }}
    >
      <Icon name="mail" style={{ width: 16, height: 16, flexShrink: 0 }} />
      <span style={{ lineHeight: 1.35 }}>
        Potwierdź adres e-mail, żeby zabezpieczyć konto i nie stracić kredytów.
      </span>
      <button
        type="button"
        onClick={handleResend}
        disabled={isSending || sent}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 12px",
          borderRadius: 999,
          border: "none",
          background: "rgba(28, 19, 0, 0.14)",
          color: "inherit",
          fontSize: 12,
          fontWeight: 700,
          cursor: isSending || sent ? "default" : "pointer",
          opacity: isSending || sent ? 0.6 : 1,
        }}
      >
        {sent ? "Wysłano ✓" : isSending ? "Wysyłam…" : "Wyślij link ponownie"}
      </button>
    </div>
  );
}
